import React from 'react';
import fetch from '@amzn/sentry-fetch';
import { createRoot, Root } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import { AvatarSize } from '@amzn/stencil-react-components/avatar';
import BaseballCardEmployeeUserAvatar from '../employee-avatars/BaseballCardEmployeeUserAvatar';
import { BaseballCardEmployee } from 'src/api/interfaces';

export const AVATAR_DIMENSIONS = {
  large: { width: 450, height: 110 },
  medium: { width: 400, height: 80 }
} as const;

export type AvatarSizeType = 'large' | 'medium';

export interface CapturedAvatarImage {
  arrayBuffer: ArrayBuffer;
  format: 'png';
}

export interface AvatarCaptureOptions {
  userInfo: BaseballCardEmployee;
  size: AvatarSizeType;
  badgeColor?: string;
  stack?: boolean;
  /**
   * Photo source (e.g. a Badge Service pre-signed S3 URL) passed straight to the Avatar.
   * Displaying it is not CORS-gated. NOTE: for html2canvas to rasterize it INTO the captured
   * PNG (useCORS), the S3 bucket must return Access-Control-Allow-Origin; without that the photo
   * degrades to the Avatar's initials in the output (no error thrown).
   */
  profileImageUrl?: string;
}

const IMAGE_FETCH_TIMEOUT_MS = 10000;

export async function fetchImageAsDataUrl(url: string): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      credentials: 'include',
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Image fetch timed out after ${IMAGE_FETCH_TIMEOUT_MS}ms`);
    }
    throw new Error(`Failed to convert image to data URL: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    clearTimeout(timeoutId);
  }
}

function createOffScreenContainer(dimensions: { width: number; height: number }): HTMLDivElement {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${dimensions.width}px`;
  container.style.height = `${dimensions.height}px`;
  document.body.appendChild(container);
  return container;
}

function renderAvatarComponent(container: HTMLDivElement, options: AvatarCaptureOptions): Root {
  const { userInfo, size, badgeColor, stack = true, profileImageUrl } = options;
  const avatarSize = size === 'large' ? AvatarSize.ExtraLarge : AvatarSize.Large;
  const root = createRoot(container);

  root.render(
    React.createElement(
      StencilProvider,
      { themeMode: ThemeMode.Light },
      React.createElement(BaseballCardEmployeeUserAvatar, {
        userInfo,
        avatarSize,
        badgeColor,
        stack,
        profileImageUrl
      })
    )
  );

  return root;
}

function waitForAvatarImageLoad(container: HTMLDivElement): Promise<void> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve();
    }, 300);

    // Use multiple selectors for more robust image detection
    // Try data-test-component first, then fall back to standard img tag
    const imgElement =
      (container.querySelector('[data-test-component="StencilAvatarImage"]') as HTMLImageElement) ||
      (container.querySelector('img[src]') as HTMLImageElement);

    if (!imgElement) {
      clearTimeout(timeout);
      resolve();
      return;
    }

    if (imgElement.complete && imgElement.naturalHeight !== 0) {
      clearTimeout(timeout);
      resolve();
      return;
    }

    imgElement.onload = () => {
      clearTimeout(timeout);
      resolve();
    };

    imgElement.onerror = () => {
      clearTimeout(timeout);
      resolve();
    };
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
  return Promise.race([promise, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(errorMessage)), timeoutMs))]);
}

/**
 * The Stencil Avatar renders the photo on a div with a built-in borderRadius design token (rounded
 * corners), and exposes no prop to disable it. For the captured PNG we want square edges matching the
 * source photo, so override the radius directly on the off-screen image element before rasterizing.
 * Scoped to the capture container only — the live on-screen Avatar is unaffected.
 */
function squareAvatarImageCorners(container: HTMLDivElement): void {
  const imgElement = container.querySelector('[data-test-component="StencilAvatarImage"]') as HTMLElement | null;
  if (imgElement) {
    imgElement.style.borderRadius = '0';
  }
}

export async function captureAvatarImage(options: AvatarCaptureOptions): Promise<CapturedAvatarImage> {
  const { size } = options;
  const dimensions = AVATAR_DIMENSIONS[size];

  let container: HTMLDivElement | null = null;
  let root: Root | null = null;

  try {
    container = createOffScreenContainer(dimensions);
    root = renderAvatarComponent(container, options);

    await withTimeout(waitForAvatarImageLoad(container), 1200, 'Avatar image load timed out');

    // Square off the Stencil Avatar's rounded corners for the captured PNG (photo case only).
    if (options.profileImageUrl) {
      squareAvatarImageCorners(container);
    }

    await new Promise((resolve) => setTimeout(resolve, 25));

    // Note: allowTaint and useCORS are mutually exclusive in html2canvas
    // useCORS is preferred as it properly handles cross-origin images
    const canvas = await html2canvas(container, {
      useCORS: true,
      backgroundColor: '#ffffff',
      width: dimensions.width,
      height: dimensions.height,
      logging: false,
      scale: 2,
      imageTimeout: 15000
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      }, 'image/png');
    });

    const arrayBuffer = await blob.arrayBuffer();

    return {
      arrayBuffer,
      format: 'png'
    };
  } finally {
    if (root) {
      root.unmount();
    }
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}
